import { AxiosError } from 'axios'
import moment from 'moment'
import { createStandardAction } from 'typesafe-actions'
import { AnyAction } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'

import { INSTALLATIONS_URL } from '../../constants/urls/backend'
import { IInstallation, INewInstallation } from '../../types/installation'
import { IRequestResult } from '../types'
import { apiRoute } from '../../utils/routeUtils'
import { axiosWithAdal } from '../../utils/http'

const installationsSuccess = createStandardAction('@@installations/SUCCESS')<IInstallation[]>()
const installationsError = createStandardAction('@@installations/ERROR')<string[]>()
const installationsRequest = createStandardAction('@@installations/REQUEST')()

const installationDeleteSuccess = createStandardAction('@@installations/DELETE_SUCCESS')<IInstallation>()
const installationDeleteError = createStandardAction('@@installations/DELETE_ERROR')<string[]>()
const installationDeleteRequest = createStandardAction('@@installations/DELETE_REQUEST')()

const installationUpdateSuccess = createStandardAction('@@installations/UPDATE_SUCCESS')<IInstallation>()
const installationUpdateError = createStandardAction('@@installations/UPDATE_ERROR')<string[]>()
const installationUpdateRequest = createStandardAction('@@installations/UPDATE_REQUEST')()

const installationCreateSuccess = createStandardAction('@@installations/CREATE_SUCCESS')<IInstallation>()
const installationCreateError = createStandardAction('@@installations/CREATE_ERROR')<string[]>()
const installationCreateRequest = createStandardAction('@@installations/CREATE_REQUEST')()

interface IResponse {
  id: number
  code: string
  description: string
  hospital_count: number
  uri: string
  created: string
  updated: string
}

const fetchInstallations = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    dispatch(installationsRequest())
    try {
      const response = await axiosWithAdal.get<IResponse[]>(apiRoute(INSTALLATIONS_URL), {
        headers: {
          'Content-Type': 'application/json',
        }
      })

      const payload: IInstallation[] = response.data.map(x => {
        return {
          id: x.id,
          code: x.code,
          description: x.description,
          uri: x.uri,
          hospitalCount: x.hospital_count,
          created: moment(x.created),
          updated: moment(x.updated),
        }
      })

      dispatch(installationsSuccess(payload))
    } catch (e) {
      dispatch(installationsError([ e.message ]))
      return
    }
  }
}

const deleteInstallation = (installation: IInstallation): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    dispatch(installationDeleteRequest())
    try {
      await axiosWithAdal.delete(`${apiRoute(INSTALLATIONS_URL)}${installation.id}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      })
      dispatch(installationDeleteSuccess(installation))
} catch (e) {
      dispatch(installationDeleteError([ e.message ]))
    }
  }
}

const updateInstallation = (installation: IInstallation): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {
    dispatch(installationUpdateRequest())
    try {
      const response = await axiosWithAdal.put<IInstallation>(
        `${apiRoute(INSTALLATIONS_URL)}${installation.id}`, JSON.stringify(installation), {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      dispatch(installationUpdateSuccess(response.data))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      dispatch(installationUpdateError([ e.message ]))
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

const createInstallation = (
  installation: INewInstallation
): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {
    dispatch(installationCreateRequest())
    try {
      const response = await axiosWithAdal.post<IInstallation>(
        apiRoute(INSTALLATIONS_URL), JSON.stringify(installation), {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      dispatch(installationCreateSuccess(response.data))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      dispatch(installationCreateError([ e.message ]))
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

export {
  fetchInstallations,
  createInstallation,
  updateInstallation,
  deleteInstallation,
  installationsSuccess,
  installationsRequest,
  installationsError,
  installationDeleteSuccess,
  installationDeleteError,
  installationDeleteRequest,
  installationCreateSuccess,
  installationCreateError,
  installationCreateRequest,
  installationUpdateSuccess,
  installationUpdateError,
  installationUpdateRequest,
}
